import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo1 from '../assets/logo1.png';
import { joinWaitlist } from '../utils/api';
import UserInfoModal from '../components/UserInfoModal';
import SuccessModal from '../components/SuccessModal';
import ContactModal from '../components/ContactModal';
import CookieConsent from '../components/CookieConsent';
import { useCountdown } from '../hooks/useCountdown';
import { useModal } from '../hooks/useModal';
import { APP_CONFIG } from '../constants/config';
import HeroSection from '../components/sections/HeroSection';
import PainPointsSection from '../components/sections/PainPointsSection';
import HowItWorksSection from '../components/sections/HowItWorksSection';
import CountdownSection from '../components/sections/CountdownSection';
import WaitlistSection from '../components/sections/WaitlistSection';
import StickyBar from '../components/ui/StickyBar';
import Footer from '../components/ui/Footer';

const BETA_PORTAL_URL = 'https://beta.wurlo.org/register';

/**
 * Home page component for the landing page
 * Manages state and orchestrates all sections
 * @param {Object} props - Component props
 * @param {number} props.spotsRemaining - Number of beta spots remaining
 * @param {Function} props.setSpotsRemaining - Function to update spots remaining
 * @param {Object} props.stats - User statistics (userCount, reviewCount, avgRating)
 */
function Home({ spotsRemaining, setSpotsRemaining, stats }) {
  // Custom hooks for modals and countdown
  const { countdown } = useCountdown(APP_CONFIG.launchDate);
  const userInfoModal = useModal();
  const successModal = useModal();
  const contactModal = useModal();
  
  // Waitlist form state
  const [formStatus, setFormStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // UI state for sticky bar
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Show sticky bar after brief delay (100ms)
  useEffect(() => {
    const timer = setTimeout(() => setShowStickyBar(true), 100);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Open waitlist modal or show error if no spots remain
   */
  const handleOpenModal = () => {
    setFormStatus('');
    userInfoModal.open();
  };

  /**
   * Submit waitlist form with error handling
   * @param {Object} userInfo - User information from form
   */
  const handleUserInfoSubmit = async (userInfo) => {
    setSubmitting(true);
    setFormStatus('');
    
    try {
      await joinWaitlist(userInfo);
      userInfoModal.close();
      setSubmitting(false);
      successModal.open();

      if (typeof window !== 'undefined') {
        const registerUrl = new URL(BETA_PORTAL_URL);
        const fullName = `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim();

        if (fullName) registerUrl.searchParams.set('fullName', fullName);
        if (userInfo.phone) registerUrl.searchParams.set('phone', userInfo.phone);
        if (userInfo.email) registerUrl.searchParams.set('email', userInfo.email);

        window.location.href = registerUrl.toString();
      }
    } catch (error) {
      setFormStatus(error.message || 'Could not join the waitlist. Please try again.');
      setSubmitting(false);
      userInfoModal.close();
    }
  };

  /**
   * Close modal (prevent closing while submitting)
   */
  const handleModalClose = () => {
    if (!submitting) userInfoModal.close();
  };

  // Format countdown for sticky bar
  const countdownText = `${countdown.days}d ${String(countdown.hours).padStart(2, '0')}:${String(countdown.minutes).padStart(2, '0')}:${String(countdown.seconds).padStart(2, '0')}`;

  return (
    <>
      <div className="min-h-screen bg-slate-50">
        {/* Header with logo */}
        <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200 shadow-sm">
          <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Primary">
            <div className="flex items-center justify-center h-16">
              <Link to="/" className="flex items-center gap-2 focus-ring" aria-label="Wurlo home">
                <img src={logo1} alt="Wurlo logo" className="h-8 w-auto" decoding="async" />
              </Link>
            </div>
          </nav>
        </header>

        {/* Main content - all sections */}
        <main id="main">
          {/* Hero section with main value proposition */}
          <HeroSection spotsRemaining={spotsRemaining} stats={stats} />

          {/* Pain points section - common tutor challenges */}
          <PainPointsSection />

          {/* How it works - 3-step process */}
          <HowItWorksSection />

          {/* Features section - all major features */}
          {/* TODO: Extract features section to separate component */}
          <section aria-labelledby="what-you-get-title" className="bg-white border-t border-b border-slate-200">
            <div className="mx-auto max-w-7xl mobile-full-width px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
              <div className="text-center max-w-3xl mx-auto mb-10">
                <h2 id="what-you-get-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
                  Tools That Transform Your Teaching
                </h2>
                <p className="text-lg sm:text-xl text-slate-700">
                  Everything you need to diagnose, plan, and teach more effectively
                </p>
              </div>
              
              <div className="grid sm:grid-cols-1 gap-4 max-w-3xl mx-auto">
                {/* Feature cards */}
                {[
                  {
                    icon: '🔍',
                    title: 'Smart Diagnostic System',
                    description: 'Quickly identify each student\'s strengths, weaknesses, and knowledge gaps with clear, actionable data.',
                  },
                  {
                    icon: '🕵️‍♂️',
                    title: 'Super Deep Diagnosis',
                    description: `Understand your student, "it's like reading their mind"`,
                  },
                  // {
                  //   icon: '🤖',
                  //   title: 'AI-Powered Lesson Plans',
                  //   description: 'Get personalized study paths and lesson plans generated automatically for each student.',
                  // },
                  // {
                  //   icon: '📈',
                  //   title: 'Progress Tracking Dashboard',
                  //   description: 'Monitor multiple students at once with visual progress reports that are easy to share with parents.',
                  // },
                  {
                    icon: '📚',
                    title: 'Resource Library',
                    description: 'Access practice problems, worksheets, and teaching materials customized to each topic.',
                  },
                  {
                    icon: '⚡',
                    title: 'Time-Saving Automation',
                    description: 'Spend less time on admin and prep, more time delivering impactful one-on-one instruction.',
                  },
                ].map((feature, index) => (
                  <div key={index} className="card-hover rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-white to-indigo-50 p-6 sm:p-8 shadow-modern-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-1">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" aria-hidden="true">
                          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.icon} {feature.title}</h3>
                        <p className="text-base text-slate-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Key features highlight section */}
          {/* TODO: Extract to separate component */}
          <section id="features" aria-labelledby="features-title" className="mx-auto max-w-7xl mobile-full-width px-4 sm:px-6 lg:px-8 py-12 sm:py-16 bg-gradient-to-br from-slate-50 via-white to-indigo-50">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 id="features-title" className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                Built for Tutors Who Value Precision & Efficiency
              </h2>
              <p className="text-lg text-slate-700">
                Powerful features that help you understand each student deeply and teach with confidence.
              </p>
            </div>
            
            <div className="relative max-w-6xl mx-auto">
              <div className="relative space-y-6">
                <div className="flex flex-col lg:flex-row gap-6 items-stretch">
                  <div className="w-full lg:w-2/3 group">
                    <div className="relative rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-2 h-full">
                      <div className="absolute top-4 right-4 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <svg width="24" height="24" className="sm:w-8 sm:h-8" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5" fill="none">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-2 sm:mb-3 pr-14">Personalized for each student</h3>
                      <p className="text-white/90 text-base sm:text-lg leading-relaxed">Every lesson plan is tailored to each student's current skill level and learning goals.</p>
                    </div>
                  </div>
                  
                  <div className="w-full lg:w-1/3 group">
                    <div className="relative rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-6 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2 h-full">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md mb-4">
                        <svg width="24" height="24" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5" fill="none">
                          <path d="M3 12s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6z"/>
                          <circle cx="12" cy="12" r="2" fill="white"/>
                        </svg>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">Diagnostic-driven insights</h3>
                      <p className="text-slate-700 text-sm sm:text-base leading-relaxed">Identify gaps instantly so you can target exactly what needs work.</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col lg:flex-row gap-6 items-stretch">
                  <div className="w-full lg:w-1/4 group">
                    <div className="relative rounded-2xl border-2 border-purple-200 bg-white p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 h-full">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md mb-3">
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path d="M12 3l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z" fill="white"/>
                        </svg>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Deep Understanding of your students</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">Know more about your students that they know about themselves</p>
                    </div>
                  </div>
                  
                  <div className="w-full lg:w-2/4 group">
                    <div className="relative rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 p-6 sm:p-8 shadow-2xl hover:shadow-3xl transition-all transform hover:-translate-y-2 h-full">
                      <div className="absolute top-4 right-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <svg width="24" height="24" className="sm:w-7 sm:h-7" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5" fill="none">
                          <path d="M7 13l3 3 7-7" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-2 sm:mb-3 pr-14">Track mastery clearly</h3>
                      <p className="text-white/95 text-base sm:text-lg leading-relaxed">See exactly when students master concepts, with clear milestones you can share with parents.</p>
                    </div>
                  </div>
                  
                  <div className="w-full lg:w-1/4 group">
                    <div className="relative rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 h-full">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md mb-3">
                        <svg width="20" height="20" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5" fill="none">
                          <path d="M4 7h16v10H4z"/>
                          <path d="M4 10h16"/>
                        </svg>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Progress assessments</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">Regular check-ins help you adjust lesson plans in real-time.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          {/* TODO: Extract to separate component */}
          <section id="faq" aria-labelledby="faq-title" className="bg-white border-t border-b border-slate-200">
            <div className="mx-auto max-w-7xl mobile-full-width px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
              <div className="text-center max-w-2xl mx-auto mb-8">
                <h2 id="faq-title" className="text-3xl sm:text-4xl font-bold text-slate-900">Frequently asked questions</h2>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    question: 'Who is Wurlo built for?',
                    answer: 'Tutors and learning centres that need a fast, trustworthy read on every student. If you care about deep diagnosis and tailored instruction, we built this for you.',
                  },
                  {
                    question: 'How deep are the diagnostics really?',
                    answer: 'Each student gets a personalised test, instant scoring, and a breakdown of strengths, gaps, and misconceptions—so detailed it feels like reading their mind.',
                  },
                  {
                    question: 'Can I repeat diagnostics as students improve?',
                    answer: 'Yes. Re-run Wurlo whenever you want to capture fresh data, compare attempts, and show progress with confidence.',
                  },
                  {
                    question: 'Does Wurlo replace my lesson planning?',
                    answer: 'Think of it as your co-pilot. Wurlo surfaces the exact topics to teach next and suggests resources, while you stay in control of the final plan.',
                  },
                  {
                    question: 'How much time will this save me each week?',
                    answer: 'Most tutors cut prep from hours to minutes because diagnostics, insight summaries, and next steps are generated automatically.',
                  },
                  {
                    question: 'Is it easy to get started?',
                    answer: 'Absolutely. Wurlo runs in the browser, no installs. Create an account and we\'ll send you straight to beta.wurlo.org with onboarding tips that take under five minutes.',
                  },
                ].map((faq, index) => (
                  <details key={index} className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-all">
                    <summary className="text-base font-semibold text-slate-900 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded flex items-center justify-between py-1">
                      <span>{faq.question}</span>
                      <svg className="chevron w-5 h-5 text-slate-400 flex-shrink-0 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </summary>
                    <div className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Beta tutor benefits section */}
          {/* TODO: Extract to separate component */}
          <section id="perks" aria-labelledby="perks-title" className="bg-gradient-to-br from-slate-50 to-white">
            <div className="mx-auto max-w-7xl mobile-full-width px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
              <div className="text-center mb-8">
                <h2 id="perks-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">
                  Beta Tutor Benefits
                </h2>
                <p className="text-lg sm:text-xl text-slate-700 max-w-3xl mx-auto">
                  Join an exclusive group of 20 lifetime-free founders shaping the future of education
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 max-w-7xl mx-auto">
                {[
                  {
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ),
                    color: 'from-indigo-500 to-purple-600',
                    title: 'Direct Line to Founder',
                    description: 'Share your tutoring challenges and needs directly with the founder. Your real-world experience shapes Wurlo\'s development.',
                  },
                  {
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
                        <path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z" fill="white" stroke="none"/>
                      </svg>
                    ),
                    color: 'from-cyan-500 to-blue-600',
                    title: 'Exclusive Post-Beta Discount',
                    description: 'Once-in-a-lifetime opportunity: Get massive discounts on lifetime access when beta launches. An offer we\'ll never repeat.',
                  },
                  {
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
                        <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ),
                    color: 'from-amber-500 to-orange-600',
                    title: 'Influence the Roadmap',
                    description: 'Your needs as a tutor directly influence what features we build next. Vote on priorities and suggest tools you need.',
                  },
                  {
                    icon: (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" fill="white"/>
                      </svg>
                    ),
                    color: 'from-emerald-500 to-teal-600',
                    title: 'Priority Beta Access',
                    description: 'Test new diagnostic tools, lesson planning features, and teaching resources before anyone else. Shape them to fit your workflow.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="card-hover rounded-3xl border-2 border-indigo-200 bg-white p-6 shadow-modern-lg">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-5 shadow-lg`}>
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                    <p className="text-base text-slate-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Waitlist signup section */}
          <WaitlistSection 
            spotsRemaining={spotsRemaining}
            onOpenModal={handleOpenModal}
            submitting={submitting}
            formStatus={formStatus}
          />

          {/* Countdown timer section */}
          <CountdownSection countdown={countdown} />
        </main>

        {/* Footer */}
        <Footer onContactClick={contactModal.open} />

        {/* Sticky bottom bar */}
        <StickyBar 
          show={showStickyBar}
          spotsRemaining={spotsRemaining}
          countdownText={countdownText}
        />

        {/* Modals for user interactions */}
        <UserInfoModal 
          isOpen={userInfoModal.isOpen}
          onClose={handleModalClose}
          onSubmit={handleUserInfoSubmit}
          submitting={submitting}
        />

        <SuccessModal 
          isOpen={successModal.isOpen}
          onClose={successModal.close}
          message="You're in! We're redirecting you to the live Wurlo beta. If nothing opens, head to beta.wurlo.org."
        />

        <ContactModal 
          isOpen={contactModal.isOpen}
          onClose={contactModal.close}
        />

        <CookieConsent />
      </div>
    </>
  );
}

export default Home;
